<template>
  <b-sidebar
    v-model="show"
    class="fulfillment-sidebar"
    id="fulfill-order-sidebar"
    width="80%"
    right
    shadow
    backdrop
    body-class=""
    no-header
    footer-class="border-top"
    @hidden="$emit('hidden')"
  >
    <div class="border-bottom pb-3 pt-3 px-2 bg-primary">
      <div class="d-flex align-items-center justify-content-between w-100">
        <b-button variant="outline-light" size="sm" v-b-toggle.fulfill-order-sidebar>
          <i class="fa fa-times"></i>
        </b-button>
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-2">Fulfill Storefront Order</h4>
          <CopyText :text="order.orderNumber" class="text-dark" />
        </div>
      </div>
    </div>
    <b-container fluid>
      <b-row>
        <b-col cols="8">
          <div class="p-3">
            <!-- Order Summary -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="card-title mb-0">Order Summary</h5>
              </div>
              <div class="card-body p-0">
                <div class="row p-3">
                  <div class="col-md-3">
                    <span class="text-muted">Order Number:</span>
                    <div class="font-weight-bold">{{ order.orderNumber }}</div>
                  </div>
                  <div class="col-md-3">
                    <span class="text-muted">Order Status:</span>
                    <div>
                      <OrderStatusBadge :status="order.orderStatus" :sent-to-fulfill-service="order.sentToFulfillService" />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <span class="text-muted">Fulfillment Status:</span>
                    <div>
                      <span v-if="isLoadingItemsToPack"><i class="fa fa-spinner fa-spin"></i></span>
                      <FulfillmentStatusBadge v-else :status="fulfillmentStatus" />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <span class="text-muted">Items Value:</span>
                    <div class="font-weight-bold">{{ currencySymbol(order.currency) }}{{ itemsToPackTotalValue.toFixed(2) }}</div>
                  </div>
                </div>

                <!-- Per-SKU fulfillment breakdown -->
                <div v-if="isLoadingItemsToPack" class="text-center py-2 text-muted">
                  <i class="fa fa-spinner fa-spin mr-1"></i> Loading items...
                </div>
                <table v-else-if="fulfillmentSummaryItems.length > 0" class="table table-bordered table-sm  mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th class="text-center">Ordered</th>
                      <th class="text-center">Packed</th>
                      <th class="text-center">To Pack</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fulfillmentSummaryItems" :key="item.sku">
                      <td><b>{{ item.sku }}</b></td>
                      <td>{{ item.name }}</td>
                      <td class="text-center">{{ item.quantityOrdered }}</td>
                      <td class="text-center">
                        <span :class="item.quantityPacked > 0 ? 'font-weight-bold text-success' : 'text-muted'">
                          {{ item.quantityPacked }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span :class="item.quantityToPack > 0 ? 'font-weight-bold text-warning' : 'text-success'">
                          {{ item.quantityToPack }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Optimal Packaging -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Packages to Label</h5>
                <div>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="loadOptimalPackaging"
                    :disabled="isLoadingOptimalPackaging"
                  >
                    <i v-if="isLoadingOptimalPackaging" class="fa fa-spinner fa-spin mr-1"></i>
                    <i v-else class="fa fa-refresh mr-1"></i>
                    {{ isLoadingOptimalPackaging ? 'Loading...' : editableBoxes && editableBoxes.length > 0 ? 'Reset (Load Optimal)' : 'Load Optimal' }}
                  </b-button>
                </div>
              </div>
              <div class="card-body">
                <div v-if="optimalBoxError" class="alert alert-danger">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  {{ optimalBoxError }}
                  <div class="mt-2">
                    <strong>Note:</strong> You can still add boxes manually using the "Add Box" section below.
                  </div>
                </div>

                <div v-else-if="allItemsAlreadyPacked" class="alert alert-warning">
                  <i class="fa fa-check-circle mr-1"></i>
                  All items for this order have already been packaged and labeled, you need to cancel existing labels if you want to redo the fulfillment.
                </div>

                <div v-else-if="editableBoxes && editableBoxes.length > 0">

                  <div>
                    <div
                      v-for="(box, index) in editableBoxes"
                      :key="box.id"
                      class="mb-3"
                    >
                      <div class="card">
                        <div class="card-header py-2 d-flex justify-content-between align-items-center">
                          <div>
                            <b-form-select
                              v-if="box.editing"
                              v-model="box.brandFulfillmentBoxId"
                              size="sm"
                              class="d-inline-block w-auto mr-2"
                              @change="onBoxTypeChanged(index)"
                            >
                              <option :value="null">Select a box type...</option>
                              <option
                                v-for="boxType in availableBoxTypes"
                                :key="boxType.id"
                                :value="boxType.id"
                              >
                                {{ boxType.name }} ({{ boxType.length }}" × {{ boxType.width }}" × {{ boxType.height }}")
                              </option>
                            </b-form-select>
                            <span v-else class="font-weight-bold">{{ box.name || `Box ${index + 1}` }}</span>
                            <span class="badge badge-primary ml-2">Box #{{ index + 1 }}</span>
                          </div>
                          <div>
                            <b-button
                              size="sm"
                              :variant="box.editing ? 'success' : 'outline-secondary'"
                              @click="toggleEditBox(index)"
                              class="mr-2"
                            >
                              <i :class="box.editing ? 'fa fa-save' : 'fa fa-edit'"></i>
                              {{ box.editing ? 'Save' : 'Edit' }}
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-danger"
                              @click="removeBox(index)"
                            >
                              <i class="fa fa-trash"></i>
                            </b-button>
                          </div>
                        </div>
                        <div class="card-body">
                          <div class="row">
                            <div class="col-md-3">
                              <div class="text-muted small">Dimensions</div>
                              <div class="font-weight-bold">
                                {{ box.length }}" × {{ box.width }}" × {{ box.height }}"
                              </div>
                            </div>
                            <div class="col-md-2">
                              <div class="text-muted small">Packaging Weight</div>
                              <div class="font-weight-bold">{{ parseFloat(box.packagingWeight).toFixed(3) }} lbs</div>
                            </div>
                            <div class="col-md-2">
                              <div class="text-muted small">Content Weight</div>
                              <div class="font-weight-bold">{{ parseFloat(box.contentWeight).toFixed(3) }} lbs</div>
                            </div>
                            <div class="col-md-2">
                              <div class="text-muted small">Total Weight</div>
                              <div class="font-weight-bold">{{ parseFloat(box.totalWeight).toFixed(3) }} lbs</div>
                            </div>
                            <div class="col-md-3">
                              <div class="text-muted small">Efficiency</div>
                              <div class="font-weight-bold">
                                {{ box.packingEfficiency ? (box.packingEfficiency * 100).toFixed(1) + '%' : 'N/A' }}
                              </div>
                            </div>
                          </div>
                          <!-- Items Section - Always Visible -->
                          <div class="mt-3 border-top pt-3">
                              <div v-if="box.items && box.items.length > 0">
                                <table class="table table-sm table-hover">
                                  <thead>
                                    <tr>
                                      <th>SKU</th>
                                      <th>Name</th>
                                      <th>Dimensions</th>
                                      <th>Weight</th>
                                      <th class="text-center">Qty</th>
                                      <th class="text-center">Unit Price</th>
                                      <th v-if="box.editing" class="text-center">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(item, itemIndex) in box.items" :key="itemIndex">
                                      <td>{{ item.sku }}</td>
                                      <td>{{ item.name }}</td>
                                      <td class="text-nowrap">
                                        {{ item.length }}" × {{ item.width }}" × {{ item.height }}"
                                      </td>
                                      <td>{{ parseFloat(item.weight).toFixed(3) }} lbs</td>
                                      <td class="text-center">
                                        <b-form-input
                                          v-if="box.editing"
                                          v-model.number="item.quantity"
                                          type="number"
                                          min="1"
                                          size="sm"
                                          class="w-auto"
                                        />
                                        <span v-else>{{ item.quantity }}</span>
                                      </td>
                                      <td class="text-center">
                                        <b-form-input
                                          v-if="box.editing"
                                          v-model.number="item.unitPrice"
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          size="sm"
                                          class="w-auto"
                                        />
                                        <span v-else>{{ currencySymbol(order.currency) }}{{ (item.unitPrice || 0).toFixed(2) }}</span>
                                      </td>
                                      <td v-if="box.editing" class="text-center">
                                        <b-button
                                          size="sm"
                                          variant="outline-danger"
                                          @click="removeItemFromBox(index, itemIndex)"
                                        >
                                          <i class="fa fa-times"></i>
                                        </b-button>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div v-else class="text-muted">
                                <b-alert show variant="danger">No items in this box</b-alert>
                              </div>
                            
                            <!-- Add Items Footer -->
                            <div class="card-footer bg-light mt-2" v-if="unpackedItems.length > 0">
                              <div class="d-flex justify-content-between align-items-center">
                                <div>
                                  <b-button
                                    size="sm"
                                    variant="success"
                                    @click="showAvailableItems(index)"
                                  >
                                    <i class="fa fa-plus mr-1"></i>
                                    Add Items ({{ unpackedItems.length }} available)
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="!isLoadingOptimalPackaging">
                  <div class="text-center text-muted py-3">
                    <i class="fa fa-box fa-2x mb-2"></i>
                    <div>Click "Load Optimal" to get recommended packaging</div>
                    <div class="mt-2 small">or add boxes manually using the "Add Box" section below</div>
                  </div>
                </div>

                <div v-if="isLoadingOptimalPackaging" class="text-center py-3">
                  <i class="fa fa-spinner fa-spin fa-2x"></i>
                  <div class="mt-2">Loading optimal packaging...</div>
                </div>
              </div>
              
              <!-- Card Footer with Add Box -->
              <div class="card-footer" v-if="!allItemsAlreadyPacked">
                <div class="d-flex align-items-center">
                  <b-form-select
                    v-model="newBoxType"
                    size="sm"
                    class="w-auto mr-2"
                  >
                    <option :value="null">Select a box type to add...</option>
                    <option
                      v-for="boxType in availableBoxTypes"
                      :key="boxType.id"
                      :value="boxType.id"
                    >
                      {{ boxType.name }} ({{ boxType.length }}" × {{ boxType.width }}" × {{ boxType.height }}")
                    </option>
                  </b-form-select>
                  <b-button
                    size="sm"
                    variant="success"
                    @click="addNewBox"
                    :disabled="!newBoxType"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Add Box
                  </b-button>
                </div>
                
                <!-- Unpacked Items Warning -->
                <div v-if="unpackedItems && unpackedItems.length > 0" class="alert alert-warning mt-3 mb-0">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  <strong>{{ unpackedItems.length }} item(s) not assigned to boxes.</strong> All items must be packed before generating rates or labels.
                </div>
              </div>
            </div>

            <!-- Carrier Rates -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Carrier Rates (Service)</h5>
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <div>
                    <b-button
                      id="btn-carrier-rate-filters"
                      variant="outline-primary"
                      size="sm"
                    >
                      <i class="fa fa-filter mr-1"></i>
                      
                      Filters
                      <b-badge v-if="hasActiveRateFilter" variant="secondary" class="ml-1">(active)</b-badge>
                    </b-button>
                    <b-popover
                      target="btn-carrier-rate-filters"
                      triggers="click blur"
                      placement="bottom"
                    >
                      <template #title>Rate Filters (from ShipCode)</template>
                      <div style="min-width: 220px;">
                        <div class="mb-2" v-if="filterBy && filterBy.rateShopId">
                          <b class="text-muted mb-1">Rate Shop: </b>
                          <span class="badge badge-warning text-truncate d-inline-block" style="max-width:180px;" :title="filterBy.rateShopId">{{ filterBy.rateShopId }}</span>
                        </div>
                        <div class="mb-2" v-else-if="filterBy && filterBy.tplCarrierCodeId">
                          <b class="text-muted mb-1">Carrier: </b>
                          <span class="badge badge-primary text-truncate d-inline-block" style="max-width:180px;">{{ filterBy.carrierName }}</span>
                        </div>
                        <div class="mb-2" v-if="filterBy && !filterBy.rateShopId && filterBy.tplCarrierServiceCodeId">
                          <b class="text-muted mb-1">Service: </b>
                          <span class="badge badge-info text-truncate d-inline-block" style="max-width:180px;">{{ filterBy.serviceName}}</span>
                        </div>
                        <div v-if="!hasActiveRateFilter" class="text-muted small mb-2">
                          <b>No filters are active</b> — all carrier rates will be shown.
                        </div>
                        <div class="d-flex justify-content-between mt-2">
                          <b-button variant="outline-secondary" size="sm" @click="clearRateFilter" :disabled="!hasActiveRateFilter">
                            Clear
                          </b-button>
                          <b-button variant="outline-primary" size="sm" @click="restoreRateFilter" :disabled="!shouldFilterBy">
                            Restore
                          </b-button>
                        </div>
                      </div>
                    </b-popover>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    @click="loadCarrierRates"
                    :disabled="isLoadingCarrierRates || !editableBoxes || editableBoxes.length === 0 || (unpackedItems && unpackedItems.length > 0)"
                  >
                    <i v-if="isLoadingCarrierRates" class="fa fa-spinner fa-spin mr-1"></i>
                    <i v-else class="fa fa-balance-scale mr-1"></i>
                    {{ isLoadingCarrierRates ? 'Loading...' : 'Get Rates' }}
                  </b-button>
                </div>
              </div>
              <div class="card-body">
                <div v-if="carrierRatesError" class="alert alert-danger">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  {{ carrierRatesError }}
                </div>

                <div v-else-if="carrierRates && carrierRates.carrierRates">
                  <div v-if="carrierRates.carrierRates && carrierRates.carrierRates.length > 0">
                    <div v-for="(carrierRate, carrierIndex) in carrierRates.carrierRates" :key="carrierIndex" class="mb-3">
                      <div class="card">
                        <div class="card-header py-2">
                          <div>
                            <strong>{{ carrierRate.carrierName }}</strong>
                            <span class="badge badge-secondary ml-2">{{ carrierRate.processingSystem }}</span>
                          </div>
                          <div class="ml-auto">
                            <span v-if="carrierRate.success" class="badge badge-success">
                              <i class="fa fa-check mr-1"></i>Success
                            </span>
                            <span v-else class="badge badge-danger">
                              <i class="fa fa-times mr-1"></i>Failed
                            </span>
                          </div>
                        </div>
                        <div class="card-body py-2">
                          <div v-if="carrierRate.success && carrierRate.rates && carrierRate.rates.length > 0">
                            <div class="row">
                              <div 
                                v-for="(rate, rateIndex) in carrierRate.rates" 
                                :key="rateIndex"
                                class="col-md-6 mb-2"
                              >
                                <div 
                                  class="border rounded p-2"
                                  :class="isRateSelected(carrierRate, rate) ? 'border-primary bg-light' : ''"
                                  @click="selectRate(carrierRate, rate)"
                                >
                                  <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div class="font-weight-bold">{{ rate.carrierCode }} - {{ rate.serviceName }}</div>
                                      <span class="text-muted">{{ rate.serviceCode }}</span>
                                    </div>
                                    <div class="text-right">
                                      <div class="font-weight-bold text-info">{{ currencySymbol(order.currency) }}{{ rate.totalCost.toFixed(2) }}</div>
                                      <span class="text-muted">
                                        {{ rate.estimatedDeliveryDays }} days
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="!carrierRate.success">
                            <div class="text-danger">
                              <i class="fa fa-exclamation-triangle mr-1"></i>
                              {{ carrierRate.errorMessage || 'Failed to get rates' }}
                            </div>
                          </div>
                          <div v-else>
                            <div class="text-muted">No rates available</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div class="text-muted text-center py-3">
                      <i class="fa fa-shipping-fast fa-2x mb-2"></i>
                      <div>No carrier rates available</div>
                    </div>
                  </div>
                </div>

                <div v-else-if="!isLoadingCarrierRates && (!editableBoxes || editableBoxes.length === 0)">
                  <div class="text-center text-muted py-3">
                    <i class="fa fa-info-circle fa-2x mb-2"></i>
                    <div>You need to have at least one package to label to pull up rates.</div>
                  </div>
                </div>
                
                <div v-else-if="!isLoadingCarrierRates && unpackedItems && unpackedItems.length > 0">
                  <div class="text-center text-muted py-3">
                    <i class="fa fa-exclamation-triangle fa-2x mb-2"></i>
                    <div>All items must be packed before getting carrier rates</div>
                  </div>
                </div>

                <div v-else-if="!isLoadingCarrierRates">
                  <div class="text-center text-muted py-3">
                    <i class="fa fa-shipping-fast fa-2x mb-2"></i>
                    <div>Click "Get Rates" to load carrier rate estimates</div>
                  </div>
                </div>

                <div v-if="isLoadingCarrierRates" class="text-center py-3">
                  <i class="fa fa-spinner fa-spin fa-2x"></i>
                  <div class="mt-2">Loading carrier rates...</div>
                </div>
              </div>
            </div>

          </div>
        </b-col>
        <b-col cols="4">
          <b-card
              bg-variant="info" text-variant="white"
              class="mt-3"
              header="Selected Service:"
              footer-class="bg-info"
            >
            <div>
              <div class="d-flex justify-content-between align-items-center"  v-if="selectedRate">
                <div>
                  <strong>{{ selectedRate.carrier.carrierName }}</strong> - {{ selectedRate.rate.serviceName }}
                  <div class="text-muted small">{{ selectedRate.rate.serviceCode }}</div>
                </div>
                <div class="text-right">
                  <div class="font-weight-bold text-warning">{{ currencySymbol(order.currency) }}{{ selectedRate.rate.totalCost.toFixed(2) }}</div>
                  <div class="text-muted small">{{ selectedRate.rate.estimatedDeliveryDays }} days</div>
                </div>
              </div>
              
              <div class="d-flex justify-content-between align-items-center" v-else>
                <div class="text-muted">No service selected</div>
                <div class="text-muted small">Please select a service to see details</div>
              </div>
            </div>
            <template #footer>
              <div class="d-flex justify-content-between">
                <b-button
                  variant="warning"
                  @click="generateLabels"
                  block
                  :disabled="isGeneratingLabels || !selectedRate || isLabelGenerated"
                >
                  <i v-if="isGeneratingLabels" class="fa fa-spinner fa-spin mr-1"></i>
                  <i v-else class="fa fa-tag mr-1"></i>
                  {{ isGeneratingLabels ? 'Generating Labels...' : 'Pack & Generate Label' }}
                </b-button>
              </div>
              
              <div v-if="labelError" class="alert alert-danger mt-3 mb-0">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                {{ labelError }}
              </div>
              
              <div v-if="isLabelGenerated" class="alert alert-success mt-3 mb-0">
                <i class="fa fa-check-circle mr-1"></i>
                <span v-if="shippingLabels && shippingLabels.length > 1">
                  {{ shippingLabels.length }} labels generated successfully!
                </span>
                <span v-else>
                  Label generated successfully! Tracking Number: {{ trackingNumber }}
                </span>
              </div>
            </template>
            </b-card>
          <div class="px-3 py-3">
          </div>
                
          <b-card class="mb-2">
            <template #header>
              <div class="d-flex w-100">
                <span>Packed and Labeled Parcels</span>
                <a
                  :href="shippingLabels && shippingLabels.length ? '/download/storefront-orders/' + order.id + '/labels/download-all' : null"
                  :target="shippingLabels && shippingLabels.length ? '_blank' : null"
                  class="btn btn-sm btn-warning ml-auto"
                  :disabled="!(shippingLabels && shippingLabels.filter(l => !l.isVoided).length)"
                  title="Download all labels"
                >
                  <i class="fa fa-download"></i> Labels + Packing List
                </a>
              </div>
            </template>
            <!-- List of Labels -->
            <OrderShipmentLabelsList
              :labels="shippingLabels"
              :loading="isLoadingLabels"
              @download="downloadLabel"
              @void="voidLabel"
            />
            <template #footer>
              <div class="alert alert-info py-2 px-3 mb-2 small">
                <i class="fa fa-info-circle mr-1"></i>
                <span v-if="order.orderStatus == 'shipped' || order.orderStatus == 'cancelled'">Order was already closed, you cannot confirm fulfillment for it. Any further changes to labels will not be send to sales channels.</span>
                <span v-else-if="fulfillmentStatus !== 'WaitingConfirmation' && fulfillmentStatus !== 'PendingFulfillment' && fulfillmentStatus !== 'ScheduledAutoconfirm'">You can only confirm an order when it has been allocated in the WMS.</span>
                <span v-else-if="fulfillmentSummaryItems.some(i => i.quantityToPack > 0)">All items must be packed before confirming.</span>
                <span v-else-if="!shippingLabels || !shippingLabels.some(l => !l.isVoided)">At least one active shipping label is required before confirming.</span>
                <span v-else-if="fulfillmentStatus == 'ScheduledAutoconfirm'">Order will be automatically marked shipped at the set time</span>
                <span v-else-if="fulfillmentStatus == 'Confirmed'">Order was fulfilled. No further actions are possible.</span>
                <span v-else-if="fulfillmentStatus == 'WaitingConfirmation'">Order was fulfilled. Confirm it to close order in WMS, eCommerce system and send Tracking Numbers to customers.</span>
                <span v-else>Awaiting Order Fulfillment</span>
              </div>
              <div class="d-flex flex-column pb-1" v-if="order.orderStatus != 'shipped' && order.orderStatus != 'cancelled'"
              :class="{ 'opacity-50': (fulfillmentStatus !== 'WaitingConfirmation' && fulfillmentStatus !== 'ScheduledAutoconfirm') || !canConfirmFulfillment }" style="gap: 6px;">
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <b-button
                    variant="primary"
                    @click="confirmFulfillment('ScheduleAutoConfirm')"
                    :disabled="!canConfirmFulfillment || isConfirmingFulfillment || fulfillmentStatus == 'ScheduledAutoconfirm' || (fulfillmentStatus != 'WaitingConfirmation')"
                    class="flex-grow-1"
                  >
                    <i v-if="isConfirmingFulfillment" class="fa fa-spinner fa-spin mr-1"></i>
                    <i v-else-if="fulfillmentStatus === 'ScheduledAutoconfirm'" class="fa fa-check mr-1"></i>
                    <i v-else class="fa fa-calendar mr-1"></i>
                    {{ fulfillmentStatus === 'ScheduledAutoconfirm' ? 'Already Scheduled' : 'Schedule Auto-Confirm' }}
                  </b-button>
                  <i
                    class="fa fa-question-circle text-muted"
                    style="cursor: default; font-size: 1.1rem; flex-shrink: 0;"
                    v-b-tooltip.hover.left="'Sets the order to ScheduledAutoconfirm status. The order will be automatically marked as shipped at end-of-day or when a manifest is processed.'"
                  ></i>
                </div>
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <b-button
                    variant="success"
                    @click="confirmFulfillment('ConfirmImmediately')"
                    :disabled="!canConfirmFulfillment || isConfirmingFulfillment || (fulfillmentStatus == 'Confirmed')"
                    class="flex-grow-1"
                  >
                    <i v-if="isConfirmingFulfillment" class="fa fa-spinner fa-spin mr-1"></i>
                    <i v-else class="fa fa-check-circle mr-1"></i>
                    Confirm Now (Mark Order Shipped)
                  </b-button>
                  <i
                    class="fa fa-question-circle text-muted"
                    style="cursor: default; font-size: 1.1rem; flex-shrink: 0;"
                    v-b-tooltip.hover.left="'Immediately marks all pending shipments as Created, updates the order status to Shipped, and triggers ORDER_SHIPPED automations (e.g. notifies Shopify/WooCommerce). NOTE: THIS ACTION CANNOT BE UNDONE!'"
                  ></i>
                </div>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
    <!-- Item Selection Modal -->
    <b-modal
      v-model="showItemSelectionModal"
      title="Select Items to Add"
      size="lg"
      @ok="addSelectedItemsToBox"
      @cancel="cancelItemSelection"
      ok-title="Add Selected Items"
      cancel-title="Cancel"
    >
      <div v-if="selectedItems && selectedItems.length > 0">
        <p class="text-muted mb-3">
          Select which items you want to add to <strong>Box #{{ currentBoxIndex + 1 }}</strong> and specify quantities:
        </p>
        
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th width="40"></th>
                <th>SKU</th>
                <th>Product Name</th>
                <th>Dimensions</th>
                <th>Weight</th>
                <th>Available</th>
                <th width="100">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in selectedItems" :key="item.externalId">
                <td>
                  <b-form-checkbox
                    v-model="item.selected"
                    @change="onItemSelectionChanged(index)"
                  />
                </td>
                <td>{{ item.sku }}</td>
                <td>{{ item.name }}</td>
                <td class="text-nowrap">{{ item.length }}" × {{ item.width }}" × {{ item.height }}"</td>
                <td>{{ parseFloat(item.weight).toFixed(3) }} lbs</td>
                <td>{{ getAvailableQuantityForItem(item.externalId) }}</td>
                <td>
                  <b-form-input
                    v-model.number="item.quantityToAdd"
                    type="number"
                    min="1"
                    :max="getAvailableQuantityForItem(item.externalId)"
                    size="sm"
                    :disabled="!item.selected"
                    :state="item.selected && item.quantityToAdd > getAvailableQuantityForItem(item.externalId) ? false : null"
                  />
                  <div v-if="item.selected && item.quantityToAdd > getAvailableQuantityForItem(item.externalId)" 
                       class="invalid-feedback d-block">
                    Max {{ getAvailableQuantityForItem(item.externalId) }} available
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="selectAllItems"
              >
                Select All
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="clearAllSelections"
                class="ml-2"
              >
                Clear All
              </b-button>
            </div>
            <div class="text-muted">
              {{ selectedItems.filter(item => item.selected).length }} of {{ selectedItems.length }} items selected
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="text-center py-4">
        <i class="fa fa-info-circle fa-2x text-muted mb-2"></i>
        <p class="text-muted">No items available to add</p>
      </div>
      
      <template #modal-footer="{ ok, cancel }">
        <b-button @click="cancel()" variant="outline-secondary">
          Cancel
        </b-button>
        <b-button 
          @click="ok()" 
          variant="primary"
          :disabled="!selectedItems.some(item => item.selected && item.quantityToAdd > 0 && item.quantityToAdd <= getAvailableQuantityForItem(item.externalId))"
        >
          Add Selected Items ({{ selectedItems.filter(item => item.selected).length }})
        </b-button>
      </template>
    </b-modal>

  </b-sidebar>
</template>

<script>
import OrderShipmentLabelsList from './OrderShipmentLabelsList.vue';
import OrderStatusBadge from './OrderStatusBadge.vue';
import FulfillmentStatusBadge from './FulfillmentStatusBadge.vue';

export default {
  name: 'FulfillOrderOverlay',
  components: { OrderShipmentLabelsList, OrderStatusBadge, FulfillmentStatusBadge },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      required: true
    },
    brandId: {
      type: String,
      required: true
    },
    warehouseId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      show: false,
      isLoadingOptimalPackaging: false,
      isLoadingCarrierRates: false,
      optimalBoxResult: null,
      optimalBoxError: null,
      carrierRates: null,
      carrierRatesError: null,
      boxItemsExpanded: {}, // Track expanded state for each box
      editableBoxes: null, // Editable copy of boxes
      allItemsAlreadyPacked: false, // All items are already packed in existing shipments
      selectedRate: null, // Selected carrier and rate
      isGeneratingLabels: false,
      isLabelGenerated: false,
      labelError: null,
      trackingNumber: null,
      isConfirmingFulfillment: false,
      shippingLabels: [],
      isLoadingLabels: false,
      availableBoxTypes: [], // List of BrandFulfillmentBoxes
      isLoadingBoxTypes: false,
      newBoxType: null, // Selected box type to add
      resolvedItemsToPack: [], // Items still needing to be packed
      fulfillmentSummaryItems: [], // Per-SKU summary for display
      fulfillmentStatus: null, // NotStarted | PartiallyPacked | FullyPacked
      isLoadingItemsToPack: false,
      // Item selection modal
      showItemSelectionModal: false,
      currentBoxIndex: null,
      selectedItems: [],
      // Rate filter (derived from order's ShipCode, can be cleared by user)
      shouldFilterBy: null,
      filterBy: null
    };
  },
  computed: {
    unpackedItems() {
      if (!this.resolvedItemsToPack || !this.editableBoxes) {
        return [];
      }
      
      // Get all resolved items to pack
      const allItems = [...this.resolvedItemsToPack];
      
      // Get packed item ExternalIds and quantities
      const packedItems = {};
      this.editableBoxes.forEach(box => {
        if (box.items) {
          box.items.forEach(item => {
            if (!packedItems[item.externalId]) {
              packedItems[item.externalId] = 0;
            }
            packedItems[item.externalId] += item.quantity;
          });
        }
      });
      
      // Find unpacked items
      return allItems.filter(item => {
        const packedQty = packedItems[item.externalId] || 0;
        return packedQty < item.quantity;
      }).map(item => ({
        ...item,
        unpackedQuantity: item.quantity - (packedItems[item.externalId] || 0)
      }));
    },
    canConfirmFulfillment() {
      const allPacked = this.fulfillmentSummaryItems.length > 0 &&
        this.fulfillmentSummaryItems.every(i => i.quantityToPack === 0);
      const hasActiveLabel = this.shippingLabels && this.shippingLabels.some(l => !l.isVoided);
      return allPacked && hasActiveLabel;
    },
    hasActiveRateFilter() {
      return this.filterBy && (
        this.filterBy.tplCarrierCodeId ||
        this.filterBy.tplCarrierServiceCodeId ||
        this.filterBy.rateShopId
      );
    },
    itemsToPackTotalValue() {
      if (!this.resolvedItemsToPack) {
        return 0;
      }
      
      return this.resolvedItemsToPack.reduce((total, item) => {
        const unitPrice = item.unitPrice || 0;
        const quantity = item.quantity || 0;
        return total + (unitPrice * quantity);
      }, 0);
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        this.show = newVal;
        if (newVal) {
          // Always load available box types and items to pack for manual operations
          this.loadBoxTypes();
          this.loadItemsToPack();
          this.loadShippingLabels();
          // Try to load optimal packaging, but don't block manual operations if it fails
          this.loadOptimalPackaging();
        }
      },
      immediate: true
    },
    show(newVal) {
      if (!newVal && this.visible) {
        this.$emit('update:visible', false);
      }
    }
  },
  methods: {
    currencySymbol(currency) {
      if (!currency || currency === 'USD') return '$';
      switch (currency.toUpperCase()) {
        case 'EUR': return 'EUR €';
        case 'GBP': return 'GBP £';
        case 'CAD': return 'CA$';
        case 'AUD': return 'A$ ';
        case 'SEK': return 'kr ';
        case 'NOK': return 'kr ';
        case 'DKK': return 'kr ';
        case 'MXN': return 'MX$ ';
        case 'BRL': return 'R$ ';
        case 'INR': return '₹ ';
        case 'KRW': return '₩ ';
        case 'SGD': return 'S$ ';
        case 'HKD': return 'HK$ ';
        case 'NZD': return 'NZ$ ';
        case 'PLN': return 'zł ';
        case 'CZK': return 'Kč ';
        case 'HUF': return 'Ft ';
        case 'RON': return 'lei ';
        default: return currency +' ';
      }
    },
    close() {
      this.show = false;
      this.$emit('update:visible', false);
      this.$emit('hidden');
    },
    toggleBoxItems(index) {
      this.$set(this.boxItemsExpanded, index, !this.boxItemsExpanded[index]);
    },
    setDefaultExpandedState() {
      if (!this.editableBoxes) {
        return;
      }
      
      // Check if there are any unpacked items
      const hasUnpackedItems = this.unpackedItems && this.unpackedItems.length > 0;
      
      // Set expanded state for each box
      this.editableBoxes.forEach((box, index) => {
        // Show items by default only if there are unpacked items
        // Hide items by default if all items are packed
        this.$set(this.boxItemsExpanded, index, hasUnpackedItems);
      });
    },
    loadOptimalPackaging() {
      this.isLoadingOptimalPackaging = true;
      this.optimalBoxError = null;
      this.optimalBoxResult = null;
      this.editableBoxes = null;
      this.allItemsAlreadyPacked = false;
      this.selectedRate = null;
      this.isLabelGenerated = false;
      this.boxItemsExpanded = {}; // Reset expanded state
      this.$http.get('/api/common/storefront-orders/' + this.order.id + '/fulfillment/optimal-packaging-box')
        .then(response => {
          this.optimalBoxResult = response.data;
          if (this.optimalBoxResult && this.optimalBoxResult.nothingLeftToPack) {
            this.allItemsAlreadyPacked = true;
            return;
          }
          // Create editable copy of boxes
          if (this.optimalBoxResult && this.optimalBoxResult.recommendedBoxes) {
            this.editableBoxes = this.optimalBoxResult.recommendedBoxes.map((box, index) => {
              return {
                ...box,
                id: box.id || `box-${Date.now()}-${index}`,
                brandFulfillmentBoxId: null, // Will need to be selected by user
                editing: false,
                volume: box.width * box.height * box.length,
                packedItemsCount: box.items ? box.items.length : 0
              };
            });
            
            // Set default expanded state after boxes are created
            this.setDefaultExpandedState();
          }
        })
        .catch(error => {
          console.error('Error getting optimal packaging box:', error);
          if (error.response && error.response.data && error.response.data.errorMessage) {
            this.optimalBoxError = error.response.data.errorMessage;
          } else {
            this.optimalBoxError = 'Failed to load optimal packaging. Please try again.';
          }
        })
        .finally(() => {
          this.isLoadingOptimalPackaging = false;
        });
    },
    loadBoxTypes() {
      this.isLoadingBoxTypes = true;
      
      // Call API to get brand fulfillment boxes using props
      const params = {
        brandId: this.brandId,
        warehouseId: this.warehouseId,
        page: 1,
        perPage: 500
      };
      
      this.$http.post('/api/manage/common/brandFulfillmentBoxes/listItems', params)
        .then(response => {
          if (response.data && response.data.items) {
            this.availableBoxTypes = response.data.items;
          }
        })
        .catch(error => {
          console.error('Error loading box types:', error);
        })
        .finally(() => {
          this.isLoadingBoxTypes = false;
        });
    },
    loadItemsToPack() {
      this.isLoadingItemsToPack = true;
      this.resolvedItemsToPack = [];
      this.fulfillmentSummaryItems = [];
      this.fulfillmentStatus = null;
      this.$http.get('/api/common/storefront-orders/' + this.order.id + '/fulfillment/status-summary')
        .then(response => {
          if (response.data && response.data.success) {
            this.resolvedItemsToPack = response.data.itemsToPack || [];
            this.fulfillmentSummaryItems = response.data.items || [];
            this.fulfillmentStatus = response.data.fulfillmentStatus || null;
            this.shouldFilterBy = response.data.shouldFilterBy || null;
            this.filterBy = this.shouldFilterBy ? Object.assign({}, this.shouldFilterBy) : null;
            console.log('this.shouldFilterBy', this.shouldFilterBy, this.filterBy)
          } else if (response.data && !response.data.success) {
            console.warn('Failed to load fulfillment status:', response.data.errorMessage);
          }
        })
        .catch(error => {
          console.error('Error loading fulfillment status:', error);
        })
        .finally(() => {
          this.isLoadingItemsToPack = false;
        });
    },
    toggleEditBox(index) {
      this.$set(this.editableBoxes[index], 'editing', !this.editableBoxes[index].editing);
      if (!this.editableBoxes[index].editing) {
        // Recalculate weights when saving
        this.recalculateBoxWeights(index);
      }
    },
    onBoxTypeChanged(index) {
      const box = this.editableBoxes[index];
      const selectedBoxType = this.availableBoxTypes.find(bt => bt.id === box.brandFulfillmentBoxId);
      
      if (selectedBoxType) {
        // Update box properties from selected type
        box.name = selectedBoxType.name;
        box.length = selectedBoxType.length;
        box.width = selectedBoxType.width;
        box.height = selectedBoxType.height;
        box.packagingWeight = selectedBoxType.weight - selectedBoxType.contentWeight; // Box weight only
        box.externalId = selectedBoxType.externalId;
        box.volume = selectedBoxType.length * selectedBoxType.width * selectedBoxType.height;
        
        // Recalculate total weights
        this.recalculateBoxWeights(index);
      }
    },
    recalculateBoxWeights(index) {
      const box = this.editableBoxes[index];
      if (box.items && box.items.length > 0) {
        box.contentWeight = box.items.reduce((sum, item) => sum + (item.weight * item.quantity), 0);
        box.totalWeight = (box.packagingWeight || 0) + box.contentWeight;
      }
    },
    getPackedQuantityForItem(externalId) {
      if (!this.editableBoxes) {
        return 0;
      }
      
      let totalPacked = 0;
      this.editableBoxes.forEach(box => {
        if (box.items) {
          box.items.forEach(item => {
            if (item.externalId === externalId) {
              totalPacked += item.quantity;
            }
          });
        }
      });
      
      return totalPacked;
    },
    getAvailableQuantityForItem(externalId) {
      const resolvedItem = this.resolvedItemsToPack.find(item => item.externalId === externalId);
      if (!resolvedItem) {
        return 0;
      }
      
      const packedQuantity = this.getPackedQuantityForItem(externalId);
      return Math.max(0, resolvedItem.quantity - packedQuantity);
    },
    addNewBox() {
      if (!this.newBoxType) {
        return;
      }
      
      const selectedBoxType = this.availableBoxTypes.find(bt => bt.id === this.newBoxType);
      if (!selectedBoxType) {
        return;
      }
      
      // Create new box with selected type
      const newBox = {
        id: `box-${Date.now()}`,
        brandFulfillmentBoxId: this.newBoxType,
        name: selectedBoxType.name,
        length: selectedBoxType.length,
        width: selectedBoxType.width,
        height: selectedBoxType.height,
        packagingWeight: selectedBoxType.weight - selectedBoxType.contentWeight,
        contentWeight: 0,
        totalWeight: selectedBoxType.weight - selectedBoxType.contentWeight,
        volume: selectedBoxType.length * selectedBoxType.width * selectedBoxType.height,
        packingEfficiency: 0,
        packedItemsCount: 0,
        items: [],
        editing: false
      };
      
      if (!this.editableBoxes) {
        this.editableBoxes = [];
      }
      
      this.editableBoxes.push(newBox);
      this.newBoxType = null; // Reset selection
      
      // Update expanded states based on current packing status
      this.setDefaultExpandedState();
      
      // Reset selected rate if boxes change
      this.selectedRate = null;
      this.isLabelGenerated = false;
    },
    removeBox(index) {
      if (confirm('Are you sure you want to remove this box?')) {
        this.editableBoxes.splice(index, 1);
        // Reset selected rate if boxes change
        this.selectedRate = null;
        this.isLabelGenerated = false;
      }
    },
    showAvailableItems(boxIndex) {
      // Get unpacked items and show selector
      if (!this.unpackedItems || this.unpackedItems.length === 0) {
        this.$swal.warning('All items are already packed');
        return;
      }
      
      // Set current box index and show modal
      this.currentBoxIndex = boxIndex;
      this.showItemSelectionModal = true;
      
      // Initialize item selection state
      this.selectedItems = this.unpackedItems.map(item => {
        const availableQty = this.getAvailableQuantityForItem(item.externalId);
        return {
          ...item,
          selected: false,
          quantityToAdd: Math.min(1, availableQty)
        };
      });
    },
    removeItemFromBox(boxIndex, itemIndex) {
      this.editableBoxes[boxIndex].items.splice(itemIndex, 1);
      this.recalculateBoxWeights(boxIndex);
    },
    onItemSelectionChanged(index) {
      const item = this.selectedItems[index];
      const availableQty = this.getAvailableQuantityForItem(item.externalId);
      
      if (!item.selected) {
        // Reset quantity to 1 when unselected
        item.quantityToAdd = Math.min(1, availableQty);
      } else {
        // Ensure quantity is at least 1 and not more than available when selected
        if (item.quantityToAdd <= 0 || item.quantityToAdd > availableQty) {
          item.quantityToAdd = Math.min(1, availableQty);
        }
      }
    },
    selectAllItems() {
      this.selectedItems.forEach(item => {
        const availableQty = this.getAvailableQuantityForItem(item.externalId);
        item.selected = availableQty > 0; // Only select items that have available quantity
        if (item.quantityToAdd <= 0 || item.quantityToAdd > availableQty) {
          item.quantityToAdd = Math.min(1, availableQty);
        }
      });
    },
    clearAllSelections() {
      this.selectedItems.forEach(item => {
        const availableQty = this.getAvailableQuantityForItem(item.externalId);
        item.selected = false;
        item.quantityToAdd = Math.min(1, availableQty);
      });
    },
    cancelItemSelection() {
      this.showItemSelectionModal = false;
      this.currentBoxIndex = null;
      this.selectedItems = [];
    },
    addSelectedItemsToBox() {
      if (this.currentBoxIndex === null) {
        return;
      }
      
      const box = this.editableBoxes[this.currentBoxIndex];
      const selectedItemsToAdd = this.selectedItems.filter(item => item.selected && item.quantityToAdd > 0);
      
      if (selectedItemsToAdd.length === 0) {
        this.$swal.warning('Please select at least one item to add');
        return;
      }
      
      // Validate quantities before adding
      const validationErrors = [];
      const itemsToProcess = [];
      
      selectedItemsToAdd.forEach(item => {
        const currentlyPackedQty = this.getPackedQuantityForItem(item.externalId);
        const resolvedItem = this.resolvedItemsToPack.find(ri => ri.externalId === item.externalId);
        const maxAvailable = resolvedItem ? resolvedItem.quantity : 0;
        
        // Check if the item already exists in this specific box
        const existingItem = box.items.find(boxItem => boxItem.externalId === item.externalId);
        const currentInThisBox = existingItem ? existingItem.quantity : 0;
        
        // Calculate the total that would be packed after this operation
        const totalAfterAdd = currentlyPackedQty + item.quantityToAdd;
        
        if (totalAfterAdd > maxAvailable) {
          const availableToAdd = maxAvailable - currentlyPackedQty;
          validationErrors.push(`${item.sku}: Cannot add ${item.quantityToAdd}, only ${availableToAdd} available (${currentlyPackedQty} already packed)`);
        } else {
          itemsToProcess.push({
            ...item,
            existingItem: existingItem,
            currentInThisBox: currentInThisBox
          });
        }
      });
      
      // Show validation errors if any
      if (validationErrors.length > 0) {
        const errorMessage = 'Quantity validation errors:\n' + validationErrors.join('\n');
        this.$swal('Error', errorMessage, 'error');
        return;
      }
      
      // Process valid items
      itemsToProcess.forEach(item => {
        if (item.existingItem) {
          // Add to existing quantity
          item.existingItem.quantity += item.quantityToAdd;
        } else {
          // Add new item to box
          box.items.push({
            id: item.id,
            externalId: item.externalId,
            sku: item.sku,
            name: item.name,
            length: item.length,
            width: item.width,
            height: item.height,
            weight: item.weight,
            unitPrice: item.unitPrice,
            quantity: item.quantityToAdd
          });
        }
      });
      
      // Update box counts and weights
      this.recalculateBoxWeights(this.currentBoxIndex);
      box.packedItemsCount = box.items.length;
      
      // Update expanded states based on new packing status
      this.setDefaultExpandedState();
      
      // Close modal and reset state
      this.showItemSelectionModal = false;
      this.currentBoxIndex = null;
      this.selectedItems = [];
      
      // Show success message
      const itemCount = itemsToProcess.length;
      const totalQuantity = itemsToProcess.reduce((sum, item) => sum + item.quantityToAdd, 0);
      this.$swal('Success', `Added ${totalQuantity} item(s) from ${itemCount} product(s) to the box`, 'success');
    },
    isRateSelected(carrier, rate) {
      return this.selectedRate && 
        this.selectedRate.carrier.carrierName === carrier.carrierName &&
        this.selectedRate.carrier.processingSystem === carrier.processingSystem &&
        this.selectedRate.rate.serviceCode === rate.serviceCode;
    },
    selectRate(carrier, rate) {
      this.selectedRate = {
        carrier: carrier,
        rate: rate
      };
      this.isLabelGenerated = false;
      this.labelError = null;
    },
    generateLabels() {
      if (!this.selectedRate) {
        alert('Please select a carrier rate first');
        return;
      }
      
      // Check if all items are packed
      if (this.unpackedItems && this.unpackedItems.length > 0) {
        this.$swal('Error', 'Please pack all items before generating labels', 'error');
        return;
      }
      
      this.isGeneratingLabels = true;
      this.labelError = null;
      
      const request = {
        storefrontOrderId: this.order.id,
        boxes: this.editableBoxes.map(box => ({
          id: box.id,
          name: box.name,
          width: box.width,
          height: box.height,
          length: box.length,
          packagingWeight: box.packagingWeight || 0,
          contentWeight: box.contentWeight || 0,
          totalWeight: box.totalWeight || 0,
          volume: box.volume,
          packingEfficiency: box.packingEfficiency || 0,
          packedItemsCount: box.packedItemsCount || 0,
          items: (box.items || []).map(item => ({
            id: item.id,
            externalId: item.externalId,
            sku: item.sku,
            name: item.name,
            length: item.length,
            width: item.width,
            height: item.height,
            weight: item.weight,
            unitPrice: item.unitPrice || 0,
            quantity: item.quantity
          }))
        })),
        carrierName: this.selectedRate.carrier.carrierName,
        processingSystem: this.selectedRate.carrier.processingSystem,
        serviceCode: this.selectedRate.rate.serviceCode,
        externalServiceId: this.selectedRate.rate.externalServiceId,
        serviceName: this.selectedRate.rate.serviceName,
        labelFormat: 'PNG',
        isFullyPackaged: true
      };
      this.$http.post('/api/common/storefront-orders/' + this.order.id + '/fulfillment/generate-label', request)
        .then(response => {
          if (response.data.success && (response.data.shipmentLabels && response.data.shipmentLabels.length > 0 || response.data.shipmentLabel)) {
            this.isLabelGenerated = true;
            
            // Handle multiple labels or single label
            const labelsToAdd = [];
            let toAddCnt = 0;
            if (response.data.shipmentLabels && response.data.shipmentLabels.length > 0) {
              // Multiple labels - one per box
              response.data.shipmentLabels.forEach((label, index) => {
                toAddCnt++;
                labelsToAdd.push({
                  id: label.id,
                  trackingNumber: label.trackingNumber,
                  carrierName: label.carrier,
                  serviceName: label.service,
                  labelUrl: label.labelUrl,
                  shipDate: label.shipDate,
                  actualLabelCost: label.actualLabelCost,
                  clientLabelCost: label.clientLabelCost,
                  boxNumber: index + 1,
                  shipment: label.shipment || null,
                  isVoided: label.isVoided
                });
              });

              this.trackingNumber = response.data.shipmentLabels[0].trackingNumber;
              this.$swal('Success', `${toAddCnt} labels generated successfully!`, 'success');
            } else if (response.data.shipmentLabel) {
              // Single label (backward compatibility)
              const label = response.data.shipmentLabel;
              labelsToAdd.push({
                id: label.id,
                trackingNumber: label.trackingNumber,
                carrierName: label.carrier,
                serviceName: label.service,
                labelUrl: label.labelUrl,
                shipDate: label.shipDate,
                actualLabelCost: label.actualLabelCost,
                clientLabelCost: label.clientLabelCost,
                shipmentStatus: label.shipmentStatus,
                shipmentLengthIn: label.shipmentLengthIn,
                shipment: label.shipment || null,
                isVoided: label.isVoided
              });

              this.trackingNumber = label.trackingNumber;
              this.$swal('Success', `Label generated successfully! Tracking: ${this.trackingNumber}`, 'success');
            }
            
            // Add all new labels to beginning of array so they appear first
            this.shippingLabels.unshift(...labelsToAdd);

            this.$emit('label-generated');

            // Refresh fulfillment summary and labels list
            this.loadItemsToPack();
            this.loadShippingLabels();
          } else {
            this.labelError = response.data.errorMessage || 'Failed to generate labels';
          }
        })
        .catch(error => {
          console.error('Error generating labels:', error);
          if (error.response && error.response.data && error.response.data.errorMessage) {
            this.labelError = error.response.data.errorMessage;
          } else {
            this.labelError = 'Failed to generate labels. Please try again.';
          }
        })
        .finally(() => {
          this.isGeneratingLabels = false;
        });
    },
    confirmFulfillment(mode) {
      const isImmediate = mode === 'ConfirmImmediately';
      const title = isImmediate ? 'Confirm & Ship Order?' : 'Schedule Auto-Confirm?';
      const text = isImmediate
        ? 'This will mark all pending shipments as Created, set the order as Shipped, and trigger shipping automations. This cannot be undone.'
        : 'The order will be scheduled to auto-confirm at end-of-day or via manifest.';

      this.$swal({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: isImmediate ? '#28a745' : '#007bff',
        cancelButtonColor: '#6c757d',
        confirmButtonText: isImmediate ? 'Yes, ship it!' : 'Yes, schedule it!'
      }).then(result => {
        if (!result.value) return;
        this.isConfirmingFulfillment = true;
        this.$http.post('/api/common/storefront-orders/' + this.order.id + '/fulfillment', { confirmationMode: mode })
          .then(response => {
            if (response.data && response.data.success) {
              this.$swal('Success', response.data.message || 'Done!', 'success');
              this.$emit('fulfillment-confirmed');
              this.loadItemsToPack();
              this.loadShippingLabels();
            } else {
              this.$swal('Error', (response.data && response.data.errorMessage) || 'Failed to confirm fulfillment', 'error');
            }
          })
          .catch(error => {
            this.$swal('Error', (error.response && error.response.data && error.response.data.errorMessage) || 'Failed to confirm fulfillment', 'error');
          })
          .finally(() => {
            this.isConfirmingFulfillment = false;
          });
      });
    },
    loadCarrierRates() {
      // Check if all items are packed
      if (this.unpackedItems && this.unpackedItems.length > 0) {
        this.$swal('Error', 'Please pack all items before getting carrier rates', 'error');
        return;
      }
      
      this.isLoadingCarrierRates = true;
      this.carrierRatesError = null;
      this.carrierRates = null;
      const ratesPayload = {
        filterBy: this.hasActiveRateFilter ? this.filterBy : null,
        boxes: this.editableBoxes.map(box => ({
          id: box.id,
          name: box.name,
          width: box.width,
          height: box.height,
          length: box.length,
          packagingWeight: box.packagingWeight || 0,
          contentWeight: box.contentWeight || 0,
          totalWeight: box.totalWeight || 0,
          packingEfficiency: box.packingEfficiency || 0,
          packedItemsCount: box.packedItemsCount || 0,
          items: (box.items || []).map(item => ({
            id: item.id,
            externalId: item.externalId,
            sku: item.sku,
            name: item.name,
            length: item.length,
            width: item.width,
            height: item.height,
            weight: item.weight,
            unitPrice: item.unitPrice || 0,
            quantity: item.quantity
          }))
        }))
      };
      this.$http.post('/api/common/storefront-orders/' + this.order.id + '/fulfillment/carrier-rates', ratesPayload)
        .then(response => {
          this.carrierRates = response.data;
        })
        .catch(error => {
          console.error('Error getting carrier rates:', error);
          if (error.response && error.response.data && error.response.data.errorMessage) {
            this.carrierRatesError = error.response.data.errorMessage;
          } else {
            this.carrierRatesError = 'Failed to load carrier rates. Please try again.';
          }
        })
        .finally(() => {
          this.isLoadingCarrierRates = false;
        });
    },
    loadShippingLabels() {
      this.isLoadingLabels = true;
      
      const params = {
        orderId: this.order.id,
        page: 1,
        perPage: 100
      };
      
      this.$http.post('/api/common/shipment-labels/listItems', params)
        .then(response => {
          if (response.data && response.data.items) {
            this.shippingLabels = response.data.items;
          }
        })
        .catch(error => {
          console.error('Error loading shipping labels:', error);
          this.shippingLabels = [];
        })
        .finally(() => {
          this.isLoadingLabels = false;
        });
    },
    downloadLabel(label) {
      window.open('/download/shipment-label/' + label.id, '_blank');
    },
    clearRateFilter() {
      this.filterBy = null;
    },
    restoreRateFilter() {
      this.filterBy = this.shouldFilterBy ? Object.assign({}, this.shouldFilterBy) : null;
    },
    voidLabel(label) {
      this.$swal({
        title: 'Void label?',
        text: 'This will cancel the label with the carrier. This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, void it!'
      }).then(result => {
        if (result.value) {
          this.$http.delete('/api/common/shipment-labels/' + label.id)
            .then(response => {
              if (response.data && response.data.success) {
                label.isVoided = true;
                this.$swal('Voided!', 'The label has been voided.', 'success');
              } else {
                this.$swal({ title: 'Error!', text: (response.data && response.data.message) || 'Failed to void label', icon: 'error' });
              }
            })
            .catch(error => {
              this.$swal({ title: 'Error!', text: (error.response && error.response.data && error.response.data.errorMessage) || 'Failed to void label', icon: 'error' });
            });
        }
      });
    },
  }
};
</script>